import { isString } from 'lodash';
import type { Fragment } from '@tiptap/pm/model';

import { TiptapNodeType } from '../tiptapTypes';

/**
 * Adds every valid label id held by a task item's `labels` attr to `labelIds`.
 *
 * Shared with the JSON-based collector in `taskListLabelUtils`, so both agree on what counts as
 * a label reference.
 */
export const addTaskItemLabelIds = (labels: unknown, labelIds: Set<string>): void => {
    if (!Array.isArray(labels)) return;

    labels.forEach((label) => {
        const labelId = (label as { labelId?: unknown } | null)?.labelId;

        if (isString(labelId) && labelId) labelIds.add(labelId);
    });
};

/** Collects the unique label ids referenced by task items in an editor content fragment. */
const getFragmentLabelIds = (fragment: Fragment, labelIds = new Set<string>()): Set<string> => {
    fragment.forEach((node) => {
        if (node.type.name === TiptapNodeType.taskItem) addTaskItemLabelIds(node.attrs.labels, labelIds);
        getFragmentLabelIds(node.content, labelIds);
    });

    return labelIds;
};

/**
 * Whether two versions of an editor's content reference a different set of task item labels.
 *
 * A task item's label chip renders from the labels registered on the owning element, which are
 * derived from the content when it saves. Until that save lands the element knows nothing of a
 * newly referenced label, so its chip cannot render - which is why a change here is worth saving
 * immediately rather than after the editor's debounce.
 */
export const haveTaskListLabelIdsChanged = ({
    previousContent,
    currentContent,
}: {
    previousContent: Fragment;
    currentContent: Fragment;
}): boolean => {
    const previousLabelIds = getFragmentLabelIds(previousContent);
    const currentLabelIds = getFragmentLabelIds(currentContent);

    if (previousLabelIds.size !== currentLabelIds.size) return true;

    return [...currentLabelIds].some((labelId) => !previousLabelIds.has(labelId));
};
